import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PositionPriceChart from './PositionPriceChart';
import AIAdvisorModal from '../AIAdvisorModal';
import { apiService } from '../../../services/api';
import { PnLBreakdown } from '../../../types/pnlBreakdown';
import {
  DisciplineByIntent,
  DisciplineState,
  DisciplineThresholdsBundle,
  Intent,
  intentLabel,
} from '../../../types/discipline';
import { buildScenarios, contractCode, explainedLines, ruleProximity, ruleRows, Scenario } from '../../../utils/positionLedger';
import { PremiumGlyph, RuleGlyph } from '../../common/Glyphs';
import { boundariesFromRules } from '../../../utils/positionBoundaries';
import { usePositionCoach } from './usePositionCoach';
import { LedgerPosition } from '../../../hooks/usePortfolioKPILoader';

const INTENTS: Intent[] = ['hold', 'wheel', 'release'];
// v3.1: a put has two intents — "Stay in Cash" is evaluated as Harvest Premium.
const PUT_INTENTS: Intent[] = ['wheel', 'release'];

const tone = (v: number | null | undefined) => (v == null ? '' : v > 0 ? 'tk-gain' : v < 0 ? 'tk-loss' : '');
/** Trader figures: "+1,176" / "−303" / "0"; null → "—". */
const n0 = (v: number | null | undefined, signed = true) => {
  if (v == null || !Number.isFinite(v)) return '—';
  const r = Math.round(v);
  return `${signed && r > 0 ? '+' : r < 0 ? '−' : ''}${Math.abs(r).toLocaleString('en-US')}`;
};
const px = (v: number | null | undefined) => (v == null || !Number.isFinite(v) ? '—' : v.toFixed(2));
const shortDate = (iso?: string | null) =>
  iso ? new Date(iso.slice(0, 10) + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—';

/** Which scenario column the rule's action points at. */
const columnFor = (action?: string): Scenario['key'] | null => {
  if (!action) return null;
  if (action === 'Roll') return 'roll';
  if (action.startsWith('Close')) return 'close';
  if (action === 'Hold' || action === 'Let Assign') return 'hold';
  return null;
};

interface Props {
  position: LedgerPosition;
  discipline: DisciplineState | null | undefined;
  thresholdsBundle: DisciplineThresholdsBundle | null;
  chartData?: { results: any[] };
  onAction: (position: any, action: string, discipline?: DisciplineState | null) => void;
  onDelete: (positionId: string) => void;
  /** Intent saved for this position: the page applies it and the fresh verdict. */
  onIntentChanged: (positionId: string, intent: Intent, discipline: DisciplineState | null) => void;
}

/**
 * Position ticket, the content of the position drawer. Decision on the left
 * (price, rules, Hold/Roll/Close grid, order), key figures on the right.
 * Gold v2 phase 3b: every figure is a backend field (the position's
 * gold.v_position row, the Discipline state with the engine's rule lines, the
 * P&L breakdown's Greeks); the ticket formats, it does not compute.
 */
const PositionCockpit: React.FC<Props> = ({
  position, discipline, thresholdsBundle, chartData, onAction, onDelete, onIntentChanged,
}) => {
  const navigate = useNavigate();
  const isCsp = position.positionType === 'cash_secured_put';
  const contracts = position.numContracts || 1;
  const currentIntent: Intent = position.intent ?? (position.keepSharesPreference === false ? 'release' : 'hold');
  const coach = usePositionCoach(position);

  // Greeks + mark freshness for this ticket only (P&L explained comes on the row).
  const [pnlBundle, setPnlBundle] = useState<PnLBreakdown | null | undefined>(undefined);
  useEffect(() => {
    let cancelled = false;
    setPnlBundle(undefined);
    apiService.getPositionPnLBreakdown(position.id)
      .then((b) => { if (!cancelled) setPnlBundle(b); })
      .catch(() => { if (!cancelled) setPnlBundle(null); });
    return () => { cancelled = true; };
  }, [position.id]);

  // ---- per-intent verdicts (read-only endpoint) ----
  const [byIntent, setByIntent] = useState<DisciplineByIntent | null>(null);
  const [byIntentFailed, setByIntentFailed] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setByIntent(null);
    setByIntentFailed(false);
    apiService.getPositionDisciplineByIntent(position.id)
      .then((r) => { if (!cancelled) setByIntent(r); })
      .catch(() => { if (!cancelled) setByIntentFailed(true); });
    return () => { cancelled = true; };
  }, [position.id]);

  // ---- plan (intent) switching: optimistic, saved, undoable ----
  const [selected, setSelected] = useState<Intent>(currentIntent);
  useEffect(() => setSelected(currentIntent), [currentIntent]);
  // The intent the engine applies (a put's "Stay in Cash" runs as Harvest).
  const shownIntent: Intent = isCsp && selected === 'hold' ? 'release' : selected;
  const [notice, setNotice] = useState<{ kind: 'saved' | 'error'; text: string; undo?: Intent } | null>(null);
  const noticeTimer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(noticeTimer.current), []);

  const switchIntent = (next: Intent, isUndo = false) => {
    if (next === selected) return;
    const prev = selected;
    setSelected(next);
    const preview = byIntent?.verdicts[next] ?? null;
    onIntentChanged(position.id, next, preview);
    apiService.updatePositionIntent(position.id, next)
      .then((res) => {
        onIntentChanged(position.id, next, res.discipline ?? preview);
        clearTimeout(noticeTimer.current);
        setNotice(isUndo ? null : {
          kind: 'saved',
          text: `Plan set to ${intentLabel(next, isCsp)} → ${res.discipline?.action ?? preview?.action ?? '—'}.`,
          undo: prev,
        });
        noticeTimer.current = setTimeout(() => setNotice(null), 8000);
      })
      .catch(() => {
        setSelected(prev);
        onIntentChanged(position.id, prev, byIntent?.verdicts[prev] ?? discipline ?? null);
        setNotice({ kind: 'error', text: `Couldn't save the plan. Still ${intentLabel(prev, isCsp)}.` });
      });
  };

  const d = discipline;
  const rows = ruleRows(d);
  const traps = d?.decision?.traps ?? [];
  const rt = d?.roll_target ?? null;
  const mark: number | null = rt?.buyback_mark ?? (position.currentPremium != null ? Number(position.currentPremium) : null);
  const scenarios = buildScenarios({
    isPut: isCsp,
    strike: position.strikePrice,
    expiry: position.expiryDate,
    gold: position,
    dte: position.daysToExpiration ?? null,
    discipline: d,
  });
  const ruleCol = columnFor(d?.action);

  const primary = (() => {
    switch (d?.action) {
      case 'Roll': return 'Review roll';
      case 'Close': case 'Close at Loss': case 'Close at Debit': return 'Review close';
      case 'Close Both': return 'Review exit';
      case 'Let Assign': return 'Review assignment';
      default: return 'Manage';
    }
  })();
  const canContinueWheel = isCsp && position.status === 'assigned';
  const runPrimary = () => {
    if (canContinueWheel) {
      navigate(`/analysis?symbol=${position.ticker}`, { state: { wheelContext: { positionId: position.id, ticker: position.ticker, assignmentStrike: position.strikePrice, shares: contracts * 100, effectiveCostBasis: position.effectiveCostBasis, currentStockPrice: position.currentPrice } } });
    } else {
      onAction(position, 'resolve', d ?? null);
    }
  };
  const primaryRef = useRef(runPrimary);
  primaryRef.current = runPrimary;

  // ⏎ runs the primary action when the drawer itself has focus
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Enter') return;
      const a = document.activeElement as HTMLElement | null;
      if (!a?.classList.contains('pdr')) return;
      if (document.querySelector('[role="dialog"]:not(.pdr)')) return;
      e.preventDefault();
      primaryRef.current();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // ---- chart lines: the engine's own (line_price / trigger_date / line_curve per rule) ----
  const boundaries = useMemo(() => boundariesFromRules(d), [d]);

  // ---- menu ----
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!menuOpen) return;
    const close = (e: MouseEvent | KeyboardEvent) => {
      if (e instanceof KeyboardEvent ? e.key === 'Escape' : !menuRef.current?.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', close);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', close); };
  }, [menuOpen]);

  // ---- side figures ----
  const g = pnlBundle?.greeks ?? null;
  // Glossary figures, one copy each (gold.v_position): stock entry = the
  // share cost (never the roll-day price), net basis = campaign-wide, net of
  // buy-backs; cost basis = the broker's number. A put's net basis is the
  // basis if assigned.
  const netBasis = position.netBasis ?? null;
  const captured = position.premiumCapturedPct ?? null;
  // The engine's time-stop date (v3.1: capped at half the trade) — never the raw setting.
  const timeStopDate = d?.rules?.find((r) => r.key === 'time_stop')?.trigger_date ?? null;
  const explained = explainedLines(position, isCsp);
  const code = (s: Scenario) => s.contract ?? '—';
  const pctFmt = (v: number | null | undefined, nd = 1) => (v == null ? '—' : `${(v * 100).toFixed(nd)}%`);

  // ---- order line ----
  const order = (() => {
    const leg = `${contracts} ${position.ticker} ${contractCode(position.expiryDate, position.strikePrice, isCsp)}`;
    if (d?.action === 'Roll' && rt) {
      const net = rt.expected_credit;
      return {
        title: 'Roll · limit',
        lines: [
          ['BTC', leg, px(mark)],
          ['STO', `${contracts} ${position.ticker} ${contractCode(rt.new_expiry, rt.new_strike, isCsp)}`, px(rt.new_price)],
        ],
        net: `${Math.abs(net).toFixed(2)} ${net >= 0 ? 'CR' : 'DB'}`,
        quote: rt.new_bid != null || rt.new_ask != null ? `${px(rt.new_bid)} / ${px(rt.new_ask)}` : null,
      };
    }
    if (d?.action === 'Close' || d?.action === 'Close at Loss' || d?.action === 'Close at Debit') {
      return { title: 'Close · limit', lines: [['BTC', leg, px(mark)]], net: mark != null ? `${mark.toFixed(2)} DB` : '—', quote: null };
    }
    if (d?.action === 'Close Both') {
      const lines: string[][] = [['BTC', leg, px(mark)]];
      if (!isCsp) lines.push(['SELL', `${contracts * 100} ${position.ticker}`, px(position.currentPrice)]);
      return { title: 'Exit · limit', lines, net: null, quote: null };
    }
    return null;
  })();

  const intents = isCsp ? PUT_INTENTS : INTENTS;
  const altPlans = byIntent ? intents.filter((i) => i !== shownIntent) : [];
  const SIDE_KV = (k: React.ReactNode, v: React.ReactNode, cls = '') => (
    <div className="tk-kv"><span className="k">{k}</span><span className={`v ${cls}`}>{v}</span></div>
  );

  return (
    <div className="tk" role="region" aria-label={`${position.ticker} position ticket`}>
      <div className="tk-split">
        <div className="tk-main">
          {/* PRICE */}
          <section className="tk-sec" aria-label="Price since entry">
            <div className="tk-h"><span>Price</span><small>since entry {shortDate(position.entryDate)}</small></div>
            <div className="tk-chart">
              <PositionPriceChart
                bars={chartData?.results}
                height={224}
                isPut={isCsp}
                strike={position.strikePrice}
                breakEven={netBasis != null && Number.isFinite(Number(netBasis)) ? Number(netBasis) : null}
                entryPrice={isCsp ? null : position.stockEntryPrice ?? null}
                entryDate={position.entryDate}
                expiryDate={position.expiryDate}
                lastPrice={position.currentPrice ?? null}
                boundaries={boundaries}
                premium={position.entryPremium ?? null}
                earningsDate={position.nextEarningsDate ?? null}
              />
            </div>
            <div className="tk-legend" aria-hidden="true">
              <span><i className="sw-zone" />{isCsp ? 'Keep-premium zone' : 'Break-even → strike'}</span>
              {boundaries?.profitTargetCurve && (
                <span title={`${isCsp ? 'Above' : 'Below'} this line the ${isCsp ? 'put' : 'call'} costs ${100 - (thresholdsBundle?.[shownIntent]?.profit_target_pct ?? 50)}% or less of what you sold it for: your profit target has fired. At expiry it sits ${isCsp ? 'below' : 'above'} the strike by the premium left to pay back.`}>
                  <i className="sw-curve is-gain" />Profit target: {isCsp ? 'above' : 'below'} the line = hit
                </span>
              )}
              {boundaries?.deltaBreachCurve && <span><i className="sw-curve is-warn" />Defensive delta: {isCsp ? 'below' : 'above'} the line = defend</span>}
              {boundaries?.strikeProximityPrice != null && (
                <span title="Keep My Shares: when the stock gets within 2% of the strike, your rule defends the shares (close or roll the call).">
                  <i className="sw-curve is-warn" />Strike proximity: above the line = defend
                </span>
              )}
              {boundaries?.profitTargetCurve && (boundaries?.deltaBreachCurve || boundaries?.strikeProximityPrice != null) && (
                <span title="Between the profit line and the defend lines no rule fires: hold. Where they meet (ACT), the hold band closes and every price fires one of your rules.">
                  Between = hold · ACT = lines meet
                </span>
              )}
              <span className="tk-legend-k">K strike · BE break-even{isCsp ? '' : ' · IN stock entry'}</span>
            </div>
          </section>

          {/* RULES */}
          <section className="tk-sec" aria-label="Your rules">
            <div className="tk-h"><span>Rules</span><small>{d?.fired_rule ? `${d.fired_rule.name} governs` : d?.flip_condition?.summary?.replace(/\.$/, '') ?? ''}</small></div>
            <div className="tk-dec">
              <span className="tk-dec-l">Your rules</span>
              {d && (() => { const px = ruleProximity(d); return <RuleGlyph progress={px.progress} fired={px.fired} />; })()}
              <span className="tk-verb" role="status" aria-live="polite">{d ? d.action : d === null ? '—' : '…'}</span>
              <div className="tk-seg" role="group" aria-label={`Plan for ${position.ticker}`}>
                {intents.map((i) => (
                  <button key={i} type="button" aria-pressed={i === shownIntent} onClick={() => switchIntent(i)}>{intentLabel(i, isCsp)}</button>
                ))}
              </div>
            </div>
            {altPlans.length > 0 && (
              <div className="tk-alt">
                {altPlans.map((i, idx) => {
                  const a = byIntent!.verdicts[i]?.action ?? '—';
                  return (
                    <React.Fragment key={i}>
                      {idx > 0 && ' · '}
                      {intentLabel(i, isCsp)} → <span className={a !== d?.action ? 'tk-warn' : ''}>{a}</span>
                    </React.Fragment>
                  );
                })}
              </div>
            )}
            {byIntentFailed && <div className="tk-alt">Other plans unavailable.</div>}
            {notice && (
              <p className={`tk-notice ${notice.kind === 'error' ? 'is-error' : ''}`} role="status">
                {notice.text}
                {notice.undo && <button type="button" onClick={() => switchIntent(notice.undo!, true)}>Undo</button>}
              </p>
            )}
            {rows.length > 0 ? (
              <table className="tk-tbl tk-rules">
                <thead><tr><th>Rule</th><th>Observed</th><th>Line</th><th>Distance</th><th>State</th></tr></thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.key} className={r.governs ? 'is-gov' : r.fired ? '' : 'is-armed'}>
                      <td>{r.name}</td>
                      <td className="n">{r.observed}</td>
                      <td className="n">{r.line}</td>
                      <td className="n">{r.distance}</td>
                      <td className={`st ${r.governs ? 'tk-sig' : ''}`}>{r.fired ? 'Fired' : 'Armed'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="tk-cap">{d === null ? "Couldn't load your rules." : d ? 'No rules apply to this position.' : 'Checking your rules…'}</p>
            )}
            {traps.map((t, i) => <p key={i} className={`tk-trap ${t.severity === 'alert' ? 'is-alert' : ''}`}>{t.message}</p>)}
          </section>

          {/* SCENARIOS */}
          <section className="tk-sec" aria-label="Hold, roll or close">
            <div className="tk-h"><span>Scenarios</span><small>Option = this leg · Position = shares + option{d?.decision?.is_approximate ? ' · ≈ latest marks' : ''}</small></div>
            <table className="tk-tbl tk-mx">
              <thead>
                <tr>
                  <th /><th />
                  {scenarios.map((s) => <th key={s.key} className={s.key === ruleCol ? 'is-on' : ''}>{s.key === 'hold' ? 'Hold' : s.key === 'roll' ? 'Roll' : 'Close'}</th>)}
                </tr>
              </thead>
              <tbody>
                <tr><td>Contract</td><td className="b" />{scenarios.map((s) => <td key={s.key} className={`n ${s.key === ruleCol ? 'is-on' : ''} ${s.contract ? '' : 'tk-dim'}`}>{code(s)}</td>)}</tr>
                <tr><td>Net cash</td><td className="b">Option</td>{scenarios.map((s) => <td key={s.key} className={`n ${s.key === ruleCol ? 'is-on' : ''} ${s.netCash ? tone(s.netCash) : 'tk-dim'}`}>{s.netCash == null ? '—' : s.netCash === 0 ? '0' : `${n0(s.netCash)} ${s.netCash > 0 ? 'cr' : 'db'}`}</td>)}</tr>
                <tr><td>P/L locked</td><td className="b">Option</td>{scenarios.map((s) => <td key={s.key} className={`n ${s.key === ruleCol ? 'is-on' : ''} ${s.locked == null ? 'tk-dim' : tone(s.locked)}`}>{n0(s.locked)}</td>)}</tr>
                <tr><td>P/L at expiry, OTM</td><td className="b">Option</td>{scenarios.map((s) => <td key={s.key} className={`n ${s.key === ruleCol ? 'is-on' : ''} ${s.atExpiry == null ? 'tk-dim' : tone(s.atExpiry)}`}>{n0(s.atExpiry)}</td>)}</tr>
                {!isCsp && (
                  <tr className="is-gap"><td>P/L if assigned</td><td className="b">Position</td>{scenarios.map((s) => <td key={s.key} className={`n ${s.key === ruleCol ? 'is-on' : ''} ${s.ifAssigned == null ? 'tk-dim' : tone(s.ifAssigned)}`}>{n0(s.ifAssigned)}</td>)}</tr>
                )}
                <tr className={isCsp ? 'is-gap' : ''}><td>Strike</td><td className="b" />{scenarios.map((s) => <td key={s.key} className={`n ${s.key === ruleCol ? 'is-on' : ''} ${s.strike == null ? 'tk-dim' : ''}`}>{s.strike == null ? '—' : s.strike}</td>)}</tr>
                <tr><td>Net Δ · DTE</td><td className="b">Position</td>{scenarios.map((s) => <td key={s.key} className={`n ${s.key === ruleCol ? 'is-on' : ''}`}>{s.netDelta ?? '—'} · {s.dte ?? '—'}</td>)}</tr>
                {position.nextEarningsDate && (
                  <tr><td>Earnings {shortDate(position.nextEarningsDate)}</td><td className="b" />{scenarios.map((s) => <td key={s.key} className={`${s.key === ruleCol ? 'is-on' : ''} ${s.earnings === 'inside' ? 'tk-warn tk-strong' : 'tk-dim'}`}>{s.earnings === 'inside' ? 'Inside' : s.earnings === 'after' ? 'After exp' : '—'}</td>)}</tr>
                )}
              </tbody>
            </table>
          </section>

          {/* ORDER */}
          {order && (
            <section className="tk-sec" aria-label="Order">
              <div className="tk-h"><span>Order</span><small>{order.title}</small></div>
              <div className="tk-ord">
                {order.lines.map(([side, what, price], i) => (
                  <div key={i}><span className="s">{side}</span><span>{what}</span><span className="p">{price}</span></div>
                ))}
                {order.net && <div className="net"><span className="s">NET</span><span>{order.net} LMT</span><span className="p">{order.quote && <><small>bid/ask</small> {order.quote}</>}</span></div>}
              </div>
            </section>
          )}
          {!order && d && (d.action === 'Hold' || d.action === 'Let Assign') && (
            <section className="tk-sec">
              <div className="tk-h"><span>Order</span><small>none</small></div>
              <p className="tk-cap">{d.action === 'Hold' ? 'Your rules hold this contract.' : `Your rules let it assign at ${position.strikePrice} if it finishes in the money.`}</p>
            </section>
          )}
        </div>

        {/* Key figures */}
        <aside className="tk-side" aria-label="Key figures">
          <div className="tk-grp">
            <div className="tk-h"><span>Position</span></div>
            {SIDE_KV('Total P&L', n0(position.totalPnl), tone(position.totalPnl))}
            {SIDE_KV('Premium captured', <span className="tk-g"><PremiumGlyph captured={captured} line={thresholdsBundle?.[shownIntent]?.profit_target_pct != null ? thresholdsBundle[shownIntent].profit_target_pct / 100 : null} />{captured != null ? `${Math.round(captured * 100)}%` : '—'} <span className="tk-dim">of {n0(position.premiumReceived, false)}</span></span>)}
            {SIDE_KV('Left to capture', n0(position.premiumLeft, false))}
            {SIDE_KV('Remaining yield', pctFmt(position.remainingYieldAnn))}
            {SIDE_KV('DTE', position.daysToExpiration ?? '—')}
            {SIDE_KV('Capital committed', n0(position.capitalCommitted, false))}
            {!isCsp && SIDE_KV('Stock entry', px(position.stockEntryPrice))}
            {!isCsp && position.costBasis != null && position.costBasis !== position.stockEntryPrice && SIDE_KV('Cost basis', px(position.costBasis))}
            {SIDE_KV(isCsp ? 'Basis if assigned' : 'Net basis', px(netBasis))}
            {SIDE_KV('Cushion', pctFmt(position.cushion))}
          </div>
          <div className="tk-grp">
            <div className="tk-h"><span>Greeks</span><small>now (entry)</small></div>
            {SIDE_KV('Net Δ', position.netDelta != null ? Math.round(position.netDelta) : '—')}
            {SIDE_KV(`Δ ${isCsp ? 'put' : 'call'}`, <>{position.currentDelta != null ? Math.abs(position.currentDelta).toFixed(2) : '—'}{position.entryDelta != null && <span className="tk-dim"> ({Math.abs(position.entryDelta).toFixed(2)})</span>}</>)}
            {SIDE_KV('Θ / day', n0(position.thetaPerDay), tone(position.thetaPerDay))}
            {SIDE_KV('Γ', g?.gamma != null ? g.gamma.toFixed(3) : '—')}
            {SIDE_KV('IV rank', <>{g?.iv_rank != null ? Math.round(g.iv_rank) : '—'}{g?.entry_iv_rank != null && <span className="tk-dim"> ({Math.round(g.entry_iv_rank)})</span>}</>)}
          </div>
          <div className="tk-grp">
            <div className="tk-h"><span>Calendar</span></div>
            {SIDE_KV('Entry', shortDate(position.entryDate))}
            {SIDE_KV('Expiry', shortDate(position.expiryDate))}
            {SIDE_KV('Time stop', timeStopDate ? shortDate(timeStopDate) : <span className="tk-dim">off</span>)}
            {SIDE_KV('Earnings', position.nextEarningsDate
              ? <>{shortDate(position.nextEarningsDate)} {position.earningsInside ? <span className="tk-warn">before exp</span> : <span className="tk-dim">after exp</span>}</>
              : '—')}
          </div>
          <div className="tk-grp">
            <div className="tk-h"><span>P&amp;L explained</span><small>this leg</small></div>
            {explained ? (
              <>
                {explained.map((l) => (
                  <React.Fragment key={l.key}>
                    {SIDE_KV(l.sub ? <span className="tk-ind">{l.label}</span> : l.label, n0(l.value), tone(l.value))}
                  </React.Fragment>
                ))}
                {SIDE_KV(<b>Total P&amp;L</b>, n0(position.totalPnl), tone(position.totalPnl))}
              </>
            ) : (
              <p className="tk-cap">No mark for this position yet.</p>
            )}
            {((position.legCount ?? 1) > 1 || position.isWheel) && SIDE_KV(`Campaign · ${position.legCount ?? 1} legs`, n0(position.campaignPnl ?? null), tone(position.campaignPnl))}
          </div>
        </aside>
      </div>

      {/* Actions */}
      <div className="tk-foot">
        <button type="button" className="tk-btn is-pri" onClick={runPrimary}>
          {canContinueWheel ? 'Continue the wheel' : primary}
          {!canContinueWheel && d?.action === 'Roll' && rt && <span className="n">{Math.abs(rt.expected_credit).toFixed(2)} {rt.expected_credit >= 0 ? 'cr' : 'db'}</span>}
          <kbd>⏎</kbd>
        </button>
        <button type="button" className="tk-btn" onClick={coach.ask} disabled={coach.loading}>{coach.loading ? 'Asking coach…' : 'Ask coach'}</button>
        <span className="tk-sp" />
        <span className="tk-keys" aria-hidden="true"><kbd>K</kbd><kbd>J</kbd> move <kbd>Esc</kbd> close</span>
        <div className="ck-more" ref={menuRef}>
          <button type="button" className="tk-btn is-icon" aria-label={`More actions for ${position.ticker}`} aria-haspopup="menu" aria-expanded={menuOpen} onClick={() => setMenuOpen((o) => !o)}>
            <svg viewBox="0 0 16 16" aria-hidden="true"><circle cx="3.5" cy="8" r="1.3" /><circle cx="8" cy="8" r="1.3" /><circle cx="12.5" cy="8" r="1.3" /></svg>
          </button>
          {menuOpen && (
            <div className="ck-menu" role="menu">
              <button type="button" role="menuitem" onClick={() => { setMenuOpen(false); onAction(position, 'manage', d ?? null); }}>All actions &amp; “no longer want this stock”…</button>
              <button type="button" role="menuitem" className="is-danger" onClick={() => { setMenuOpen(false); if (window.confirm(`Delete the ${position.ticker} position? This removes it and its history from Babylon.`)) onDelete(position.id); }}>Delete position…</button>
            </div>
          )}
        </div>
      </div>

      <AIAdvisorModal
        isOpen={coach.open}
        onClose={coach.close}
        ticker={position.ticker}
        isLoading={coach.loading}
        error={coach.error}
        recommendation={coach.recommendation}
        onRetry={coach.ask}
      />
    </div>
  );
};

export default PositionCockpit;
